import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { LayoutService } from '../../core/layout.service';
import { MenuComponent } from '../../../kt/components';
import { AccountsContextService } from 'src/app/accounts/account-context.service';
import { PageInfoService } from '../../core/page-info.service';
import { AdvertiserPaymentsService } from 'src/app/advertiser/payments/advertiser-payments.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ktPageTitle', { static: true })
  public ktPageTitle: ElementRef;

  public headerContainerCssClasses: string = '';
  public asideDisplay: boolean = true;
  public headerLeft: string = 'menu';
  public pageTitleCssClasses: string = '';
  public pageTitleAttributes: {
    [attrName: string]: string | boolean;
  };
  public totalFunds: number | null;
  public isAdvertiserUrl: boolean = false;

  public readonly router: Router;
  public readonly pageInfoService: PageInfoService;
  public readonly advertiserPaymentsService: AdvertiserPaymentsService;

  private readonly layout: LayoutService;
  private readonly contextService: AccountsContextService;
  private readonly cdr: ChangeDetectorRef;

  private advertiserId: string;
  private previousAdvertiserId: string | null = null;
  private subscription: Subscription = new Subscription();

  constructor() {
    this.router = inject(Router);
    this.pageInfoService = inject(PageInfoService);
    this.advertiserPaymentsService = inject(AdvertiserPaymentsService);
    this.layout = inject(LayoutService);
    this.contextService = inject(AccountsContextService);
    this.cdr = inject(ChangeDetectorRef);

    this.routingChanges();
    this.isAdvertiserPage();
  }

  public get homeRedirectRoute$() {
    return this.contextService.homeRedirectRoute$
  };

  public ngOnInit(): void {
    this.extractAdvertiserId();
    this.headerContainerCssClasses =
      this.layout.getStringCSSClasses('headerContainer');
    this.asideDisplay = this.layout.getProp('aside.display') as boolean;
    this.headerLeft = this.layout.getProp('header.left') as string;
    this.pageTitleCssClasses = this.layout.getStringCSSClasses('pageTitle');
    this.pageTitleAttributes = this.layout.getHTMLAttributes('pageTitle');
  }

  public ngAfterViewInit(): void {
    if (this.ktPageTitle) {
      for (const key in this.pageTitleAttributes) {
        if (Object.hasOwn(this.pageTitleAttributes, key)) {
          this.ktPageTitle.nativeElement.attributes[key] =
            this.pageTitleAttributes[key];
        }
      }
    }
  }

  public onClickLogo(): void {
    const copyCurrentRoute = this.contextService.currentRoute;
    this.contextService.homeRedirectRoute$
      .pipe(take(1))
      .subscribe(
        homeUrl => {
          if (homeUrl === copyCurrentRoute) {
            location.reload();
          }
        }
      );
  }

  public routingChanges(): void {
    const routerSubscription$ = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        MenuComponent.reinitialization();
      }
    });
    this.subscription.add(routerSubscription$);
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private extractAdvertiserId(): void {
    const urlParts: string[] = this.router.url.split('/');
    const advertiserIndex: number = urlParts.indexOf('advertiser');
    if (advertiserIndex !== -1 && advertiserIndex + 1 < urlParts.length) {
      this.advertiserId = urlParts[advertiserIndex + 1];
      this.getFunds(this.advertiserId);
    }
  }

  private getFunds(advertiserId: string): void {
    if (advertiserId !== this.previousAdvertiserId) {
      this.previousAdvertiserId = advertiserId;
      this.advertiserPaymentsService.fetchFundsSummary(advertiserId);

      const fundsStream$: Subscription = this.advertiserPaymentsService.totalFunds$
        .subscribe((totalFunds: number | null) => {
          this.totalFunds = totalFunds;
          this.cdr.detectChanges();
        });
      this.subscription.add(fundsStream$);
    }
  }

  private isAdvertiserPage(): void {
    const routeSubscription$: Subscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.extractAdvertiserId();
        this.isAdvertiserUrl = event.url.includes('advertiser');
      }
    });
    this.subscription.add(routeSubscription$);
  }
}
